import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SCROLL_DOWN_THRESHOLD = 8; // compact almost immediately — the moment the user starts scrolling down
const SCROLL_UP_DELTA = 6; // small upward delta restores full size

/**
 * Returns `true` while the user is scrolling down past a small threshold, and
 * flips back to `false` as soon as they scroll back up. Used by bottom navs
 * to compact icons when the user is reading content, giving back screen real
 * estate without hiding navigation entirely.
 *
 * Auto-detects the scroll target: prefers `[data-jump-scroll-container="true"]`
 * if present on the current page (e.g. Guest Map's inner scroll area), else
 * falls back to `window` scroll. Re-resolves on route changes.
 */
export const useScrollNavCompact = () => {
  const [compact, setCompact] = useState(false);
  const lastYRef = useRef(0);
  const location = useLocation();

  useEffect(() => {
    // Listen on BOTH the inner scroll container (when one exists for the
    // current page) AND the window. Whichever surface actually scrolls
    // updates the compact state — handles both layout modes (window-scroll
    // pages and inner-scroll-container pages).
    const containerEl = document.querySelector('[data-jump-scroll-container="true"]');
    const getY = () => {
      const containerY = containerEl ? containerEl.scrollTop : 0;
      const winY = window.scrollY || window.pageYOffset || 0;
      return Math.max(containerY, winY);
    };

    lastYRef.current = getY();
    let ticking = false;
    const update = () => {
      const y = getY();
      const last = lastYRef.current;
      if (y > last + 2 && y > SCROLL_DOWN_THRESHOLD) {
        setCompact(true);
      } else if (y < last - SCROLL_UP_DELTA || y < 4) {
        // Only restore to full size when the user is essentially back at
        // the top. With the small compact threshold above, comparing to
        // half of the threshold would cause the nav to flap on tiny
        // scroll deltas.
        setCompact(false);
      }
      lastYRef.current = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    containerEl?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      containerEl?.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname]);

  return compact;
};

export default useScrollNavCompact;
